import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, ShieldOff, ArrowLeft, Search, FileText, Globe, Bot, Zap, MapPin, Sparkles, ArrowRight, Star } from 'lucide-react';
import IndexNowPanel from '@/components/admin/marketing/IndexNowPanel';
import LocalSeoPanel from '@/components/admin/marketing/LocalSeoPanel';
import TripAdvisorPanel from '@/components/admin/marketing/TripAdvisorPanel';
import { Link } from 'react-router-dom';
import PriceCheckPanel from '@/components/admin/marketing/PriceCheckPanel';
import ProductSeoPanel from '@/components/admin/marketing/ProductSeoPanel';
import PageSeoPanel from '@/components/admin/marketing/PageSeoPanel';
import MarketingAgentChat from '@/components/admin/marketing/MarketingAgentChat';
import SearchConsolePanel from '@/components/admin/marketing/SearchConsolePanel';
import { useAuth } from '@/lib/AuthContext';

const TABS = [
  { key: 'queries', label: 'TOP QUERIES', icon: Search },
  { key: 'price', label: 'PRICE CHECK', icon: Search },
  { key: 'product-seo', label: 'PRODUCT SEO', icon: FileText },
  { key: 'page-seo', label: 'PAGE SEO', icon: Globe },
  { key: 'agent', label: 'AI ASSISTANT', icon: Bot },
  { key: 'local', label: 'LOCAL SEO', icon: MapPin },
  { key: 'indexnow', label: 'INDEXNOW', icon: Zap },
  { key: 'tripadvisor', label: 'TRIPADVISOR', icon: Star },
];

export default function AdminMarketing() {
  const { user, isLoadingAuth: loading } = useAuth();
  const [tab, setTab] = useState('queries');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          {!user && <Link to="/admin" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">SIGN IN →</Link>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-10 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">MARKETING &amp; COMMS</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Marketing &amp; SEO</h1>
          <p className="text-white/40 text-sm mt-2 max-w-2xl">Check competitor pricing, generate AI-optimised SEO for products, the homepage and category pages, and chat to the marketing AI assistant.</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2.5 border text-xs font-bold tracking-widest transition-all ${
                  tab === t.key ? 'bg-primary text-white border-primary' : 'bg-primary/5 border-primary/30 text-white hover:border-primary hover:bg-primary/10'
                }`}
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-6xl mx-auto">
        {/* SEO workflow guide — ties the tabs into one cohesive surface */}
        <div className="border border-primary/20 bg-primary/[0.03] p-5 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <p className="text-primary text-[10px] font-bold tracking-widest">SEO WORKFLOW</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { n: 1, icon: Search, tab: 'queries', title: 'Generate an AI action plan', body: 'Load your Search Console data, then click "Fix my SEO with AI" for a prioritised task list you can action one by one.' },
              { n: 2, icon: FileText, tab: 'product-seo', title: 'Apply page & product SEO', body: 'Generate optimised titles and descriptions for products and pages, then mark each task done.' },
              { n: 3, icon: Zap, tab: 'indexnow', title: 'Submit to IndexNow', body: 'Tell Bing & Yandex to re-crawl your updated pages instantly once you publish changes.' },
              { n: 4, icon: MapPin, tab: 'local', title: 'Run local citation checks', body: 'Audit your local SEO listings and citations for NAP consistency across directories.' },
            ].map((s) => (
              <button key={s.n} onClick={() => setTab(s.tab)} className="text-left border border-white/10 bg-[#0d0d0f] hover:border-primary/40 p-4 transition-colors group">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 bg-primary/15 text-primary font-black text-xs flex items-center justify-center">{s.n}</span>
                  <s.icon className="w-4 h-4 text-white/40 group-hover:text-primary transition-colors" />
                </div>
                <p className="text-white font-bold text-xs mb-1">{s.title}</p>
                <p className="text-white/40 text-[11px] leading-relaxed">{s.body}</p>
                <span className="inline-flex items-center gap-1 text-primary text-[10px] font-bold tracking-widest mt-2 group-hover:gap-1.5 transition-all">OPEN <ArrowRight className="w-3 h-3" /></span>
              </button>
            ))}
          </div>
        </div>
        {tab === 'queries' && <SearchConsolePanel />}
        {tab === 'price' && <PriceCheckPanel />}
        {tab === 'product-seo' && <ProductSeoPanel />}
        {tab === 'page-seo' && <PageSeoPanel />}
        {tab === 'agent' && <MarketingAgentChat />}
        {tab === 'local' && <LocalSeoPanel />}
        {tab === 'indexnow' && <IndexNowPanel />}
        {tab === 'tripadvisor' && <TripAdvisorPanel />}
      </section>
    </div>
  );
}